import { useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button, NumberInput,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, TagsInput, MultiSelect,
} from '@mantine/core'
import { IconPlus, IconPencil, IconTrash, IconPhoto, IconEye, IconEyeOff, IconArrowUp, IconArrowDown } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { usePackages, useBranches } from '@/lib/swr'
import type { Package } from '@/types'

interface PackageFormData {
  name: string
  badge: string
  badgeColor: string
  description: string
  price: number
  prepaymentAmount: number
  duration: number
  features: string[]
  image: string
  branches: string[]
  order: number
}

const EMPTY_FORM: PackageFormData = {
  name: '', badge: '', badgeColor: '',
  description: '', price: 0, prepaymentAmount: 0, duration: 0,
  features: [], image: '', branches: [], order: 0,
}

export default function PackageList() {
  const { data, isLoading, mutate } = usePackages({ limit: '100' })
  const packages: Package[] = (data?.data ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const { data: branchesData } = useBranches()
  const branches = branchesData?.data ?? []
  const branchOptions = branches.map((b) => ({ value: b._id, label: b.name }))
  const getBranchName = (id: string) => branches.find((b) => b._id === id)?.name ?? id

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PackageFormData>({ defaultValues: EMPTY_FORM })

  const watchImage = watch('image')

  const openCreate = () => { setEditId(null); reset(EMPTY_FORM); setModalOpen(true) }

  const openEdit = (p: Package) => {
    setEditId(p._id)
    reset({
      name: p.name, badge: p.badge, badgeColor: p.badgeColor,
      description: p.description, price: p.price, prepaymentAmount: p.prepaymentAmount,
      duration: p.duration, features: p.features || [], image: p.image,
      branches: p.branches || [], order: p.order ?? 0,
    })
    setModalOpen(true)
  }

  const closeModal = () => { setModalOpen(false); reset(EMPTY_FORM) }

  const onSubmit = handleSubmit(async (form) => {
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      prepaymentAmount: Number(form.prepaymentAmount) || 0,
      duration: Number(form.duration) || 0,
      order: Number(form.order) || 0,
    }
    try {
      if (editId) await api.admin.updatePackage(editId, payload)
      else await api.admin.createPackage(payload)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      closeModal(); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const remove = (id: string) => confirmDelete('Энэ багцыг устгах уу?', async () => {
    try {
      await api.admin.deletePackage(id)
      toast.success('Устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggleStatus = async (p: Package) => {
    try { await api.admin.updatePackageStatus(p._id, !p.isActive); mutate() }
    catch (err: any) { toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа') }
  }

  const moveOrder = async (pkg: Package, direction: 'up' | 'down') => {
    const idx = packages.findIndex((p) => p._id === pkg._id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= packages.length) return
    try {
      await Promise.all([
        api.admin.updatePackage(packages[idx]._id, { order: packages[swapIdx].order ?? swapIdx }),
        api.admin.updatePackage(packages[swapIdx]._id, { order: packages[idx].order ?? idx }),
      ])
      mutate()
    } catch { toast.error('Дараалал солиход алдаа гарлаа') }
  }

  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('image', result.url, { shouldDirty: true })
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally { setUploading(false) }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Багц эмчилгээ</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Багц нэмэх</Button>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={50}>#</Table.Th>
              <Table.Th>Зураг</Table.Th>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Салбар</Table.Th>
              <Table.Th>Үнэ</Table.Th>
              <Table.Th>Хугацаа</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={150}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {packages.map((p, idx) => (
              <Table.Tr key={p._id}>
                <Table.Td>
                  <Group gap={2}>
                    <ActionIcon variant="subtle" size="xs" onClick={() => moveOrder(p, 'up')} disabled={idx === 0}>
                      <IconArrowUp size={12} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" size="xs" onClick={() => moveOrder(p, 'down')} disabled={idx >= packages.length - 1}>
                      <IconArrowDown size={12} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
                <Table.Td>
                  {p.image ? <img src={p.image} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} /> : <Text c="dimmed" size="xs">—</Text>}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{p.name}</Text>
                  {p.badge && <Badge size="xs" variant="light" mt={2}>{p.badge}</Badge>}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {(p.branches || []).map((bId) => (
                      <Badge key={bId} variant="light" size="xs" color="blue">{getBranchName(bId)}</Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{p.price.toLocaleString()}₮</Text>
                  {p.prepaymentAmount > 0 && p.prepaymentAmount !== p.price && (
                    <Text size="xs" c="dimmed" td="line-through">{p.prepaymentAmount.toLocaleString()}₮</Text>
                  )}
                </Table.Td>
                <Table.Td><Text size="sm">{p.duration} хоног</Text></Table.Td>
                <Table.Td>
                  <Badge color={p.isActive ? 'green' : 'gray'} variant="light" size="sm">
                    {p.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" size="sm" onClick={() => toggleStatus(p)}>
                      {p.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                    </ActionIcon>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(p)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(p._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {packages.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={8}><Text ta="center" c="dimmed" py="lg">Багц бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Багц засах' : 'Шинэ багц'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
              render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />}
            />
            <Group grow>
              <Controller
                name="badge"
                control={control}
                render={({ field }) => <TextInput label="Badge" placeholder="жнь: Шинэ" {...field} />}
              />
              <Controller
                name="badgeColor"
                control={control}
                render={({ field }) => <TextInput label="Badge өнгө" placeholder="жнь: green" {...field} />}
              />
            </Group>
            <Controller
              name="branches"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Салбар"
                  placeholder="Салбар сонгох"
                  data={branchOptions}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  searchable
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />}
            />
            <Group grow>
              <Controller
                name="prepaymentAmount"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Үндсэн үнэ" description="Хямдрахаас өмнөх үнэ ₮" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} thousandSeparator="," />
                )}
              />
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Хямдарсан үнэ" description="Төлөх үнэ ₮" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} thousandSeparator="," />
                )}
              />
              <Controller
                name="duration"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Хугацаа" description="Хоногийн тоо" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />
                )}
              />
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Дараалал" description="Бага тоо = эхэнд" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />
                )}
              />
            </Group>
            <Controller
              name="features"
              control={control}
              render={({ field }) => (
                <TagsInput
                  label="Онцлогууд"
                  placeholder="Enter дарж нэмэх"
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'; input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                    input.click()
                  }}
                >Upload</Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }} />}
            </div>
            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
